import React, {
    useCallback,
    useReducer,
    useMemo,
    useContext,
    useEffect,
} from 'react';
import { withCookies } from 'react-cookie';
import { useMediaQuery } from 'react-responsive';

import Input from 'components/bronson/Input';
import Button from 'components/bronson/Button';
import Traductor from 'components/Traductor';

import VehicleService from 'services/VehicleService';
import { UserContext } from 'contexts/UserContext';
import Globals from '../Globals';

const initialVehicleStatusPageState = {
    loading: false,
    plate: '',
    plateFormatError: false,
    content: '',
};

const vehicleStatusPageReducer = (state, { type, payload }) => {
    switch (type) {
        case 'UPDATE_STATUS':
            return { ...state, loading: !state.loading };
        case 'UPDATE_PLATE':
            return {
                ...state,
                plate: payload,
                plateFormatError:
                    payload &&
                    !payload.match(
                        /^\d{4}[b-df-hj-np-tv-zB-DF-HJ-NP-TV-Z]{3}$/
                    ),
            };
        case 'SET_VEHICLE_STATUS':
            return { ...state, content: payload };
        default:
            return state;
    }
};

const VehicleStatusPage = ({ cookies, history }) => {
    const t = useMemo(() => new Traductor(), []);
    const [
        { plate, loading, content, plateFormatError },
        dispatch,
    ] = useReducer(vehicleStatusPageReducer, initialVehicleStatusPageState);
    const { user } = useContext(UserContext);
    const isMobile = useMediaQuery({
        maxDeviceWidth: 600,
    });
    const searchButtonOptions = useMemo(
        () => ({
            loading,
            disabled: loading || !plate || plateFormatError,
            text: isMobile ? '' : t.getTraduction('Search'),
            name: t.getTraduction('Search'),
            btnTypes: [isMobile ? 'icon' : 'simple'],
            class: isMobile ? 'u-3/12' : 'u-1/12 u-ml',
            icon: isMobile
                ? {
                      title: 'search',
                      image: 'semantic-search',
                      isBeforeText: true,
                  }
                : {},
        }),
        [isMobile, loading, plate, plateFormatError, t]
    );

    useEffect(() => {
        if (user.data.Profile !== 12) {
            history.push(Globals.APPLICATION_PATH.HOME);
        }
    }, [history, user.data.Profile]);

    const fetchVehicleStatus = useCallback(async () => {
        dispatch({ type: 'UPDATE_STATUS' });
        try {
            const response = await new VehicleService().getVehicleInformation(
                cookies.get(Globals.COOKIES.JWT)?.AccessToken,
                {
                    plate,
                    dealers: user?.data?.Dealers?.map(e => e.VWFCode) || [],
                }
            );
            if (response?.body) {
                const parsedResponse = JSON.parse(response.body.Result);
                if (parsedResponse) {
                    dispatch({
                        type: 'SET_VEHICLE_STATUS',
                        payload: parsedResponse || '',
                    });
                }
            }
        } catch (error) {}
        dispatch({ type: 'UPDATE_STATUS' });
    }, [cookies, plate, user]);

    return (
        <section className="o-content-section u-pt-small u-rema-full-height">
            <div className="o-page-wrap o-page-wrap--xsmall">
                <header className="o-layout u-mb-large">
                    <div className="o-layout__item u-1/1">
                        <h1>{t.getTraduction('CheckVehicleStatus')}</h1>
                    </div>
                </header>
                <div className="u-mb-large">
                    <label
                        htmlFor={t.getTraduction('Plate')}
                        className={'c-form-field__label '}
                    >
                        {t.getTraduction('Plate')}
                    </label>
                    <div
                        className={`o-inline-group u-m-none u-mt u-1/1`}
                        style={{
                            justifyContent: isMobile
                                ? 'space-between'
                                : 'unset',
                        }}
                    >
                        <Input
                            value={plate}
                            options={{
                                id: t.getTraduction('Plate'),
                                loading,
                                type: 'text',
                                event: false,
                                className: plateFormatError ? 'is-error' : '',
                                container: {
                                    className: 'u-3/12 u-mr u-8/12@s',
                                },
                            }}
                            onChange={payload =>
                                dispatch({ type: 'UPDATE_PLATE', payload })
                            }
                        />
                        <Button
                            options={searchButtonOptions}
                            onClick={fetchVehicleStatus}
                        />
                    </div>
                    {plateFormatError && (
                        <p className="c-error-message">
                            {t.getTraduction('PlateFormatError')}
                        </p>
                    )}
                </div>
                {content && <p className="u-mt u-1/1">{content}</p>}
            </div>
        </section>
    );
};

export default withCookies(VehicleStatusPage);
